import React from 'react';
import { Currency } from 'ui-toolkit';
import { useSelectCurrentOrder } from '../../../selectors/order/selectCurrentOrder';
import { BodyTextTW } from '../../../tailwindComponents/bodyText/BodyTextTW';
import { useGetEscrowBalancesDataQuery } from '../../../API/order/escrow/EscrowBaseAPI';
import { eTransactionCategory } from '../../../ui-toolkit/enums/eTransactionType';

export interface IFeesDetailsHeaderProps {
  orderId: string;
}

const FeeDetailsHeaderValues: React.FC<{
  label: string;
  value: number | null | undefined;
  children?: React.ReactNode;
}> = ({ children, label, value }) => {
  return (
    <div className={'flex flex-row pr-4'}>
      <BodyTextTW className={'text-gray-400'}>{label}</BodyTextTW>
      <BodyTextTW className={'pl-2 font-bold'}>{children}</BodyTextTW>
    </div>
  );
};

export const FeesDetailsHeader: React.FC<IFeesDetailsHeaderProps> = ({ orderId }) => {
  const { data: orderDetails } = useSelectCurrentOrder();
  const { data: balances } = useGetEscrowBalancesDataQuery(orderId);

  const transactionType = orderDetails.transaction.transaction_category;
  const loanAmount = orderDetails.transaction.loan_amount;
  const purchasePrice = orderDetails.transaction.purchase_price;
  const borrowerMoney = balances?.expectedBorrowerCashToCloseAmountDollars;
  const buyerMoney = balances?.expectedBuyerCashToCloseAmountDollars;
  const lenderMoney = balances?.expectedIncomingLenderAmountDollars;
  const sellerMoney = balances?.expectedSellerCashToCloseAmountDollars;
  const actual = balances?.actualAmountDollars;
  const available = balances?.availableAmountDollars;
  const totalDeposits = balances?.depositsTotalAmountDollars;
  const totalDisbursements = balances?.disbursementsTotalAmountDollars;
  return (
    <div className={'flex flex-row border-y'}>
      <div className={'pl-3'}>
        <BodyTextTW>Loan Details</BodyTextTW>
        <div className={'flex flex-row '}>
          <FeeDetailsHeaderValues label={'Loan'} value={loanAmount}>
            {loanAmount ? <Currency data-test-id={'loan-amount-value'} value={loanAmount} /> : '--'}
          </FeeDetailsHeaderValues>
          {transactionType === 'purchase' && (
            <FeeDetailsHeaderValues label={'Purchase'} value={purchasePrice}>
              {purchasePrice ? <Currency data-test-id={'purchase-price-value'} value={purchasePrice} /> : '--'}
            </FeeDetailsHeaderValues>
          )}
        </div>
      </div>
      <div className={'border-x'}>
        <BodyTextTW className={'pl-2'}>Expected</BodyTextTW>
        <div className={'flex flex-row pl-2'}>
          {transactionType === 'purchase' && (
            <>
              <FeeDetailsHeaderValues label={'Buyer'} value={buyerMoney}>
                {buyerMoney && buyerMoney > 0 ? (
                  <Currency data-test-id={'buyer-money-value'} value={buyerMoney} />
                ) : (
                  '--'
                )}
              </FeeDetailsHeaderValues>
              <FeeDetailsHeaderValues label={'Seller'} value={sellerMoney}>
                {sellerMoney && sellerMoney > 0 ? (
                  <Currency data-test-id={'seller-money-value'} value={sellerMoney} />
                ) : (
                  '--'
                )}
              </FeeDetailsHeaderValues>
            </>
          )}
          {transactionType === eTransactionCategory.Refinance && (
            <FeeDetailsHeaderValues label={'Borrower'} value={borrowerMoney}>
              {borrowerMoney && borrowerMoney < 0 ? (
                <Currency data-test-id={'borrower-money-value'} value={borrowerMoney * -1} />
              ) : (
                '--'
              )}
            </FeeDetailsHeaderValues>
          )}
          <FeeDetailsHeaderValues label={'Lender'} value={lenderMoney}>
            {loanAmount !== null && loanAmount > 0 ? (
              <Currency data-test-id={'lender-money-value'} value={lenderMoney} />
            ) : (
              '--'
            )}
          </FeeDetailsHeaderValues>
        </div>
      </div>
      <div>
        <BodyTextTW className={'pl-2'}>Balances</BodyTextTW>
        <div className={'flex flex-row pl-2'}>
          <FeeDetailsHeaderValues label={'Actual'} value={actual}>
            {actual !== 0 ? <Currency data-test-id={'actual-value'} value={actual} /> : '--'}
          </FeeDetailsHeaderValues>
          <FeeDetailsHeaderValues label={'Available'} value={available}>
            {available !== 0 ? <Currency data-test-id={'available-value'} value={available} /> : '--'}
          </FeeDetailsHeaderValues>
          <FeeDetailsHeaderValues label={'Deposits'} value={totalDeposits}>
            {totalDeposits !== 0 ? <Currency data-test-id={'deposits-value'} value={totalDeposits} /> : '--'}
          </FeeDetailsHeaderValues>
          <FeeDetailsHeaderValues label={'Disbursements'} value={totalDisbursements}>
            {totalDisbursements !== 0 ? (
              <Currency data-test-id={'disbursements-value'} value={totalDisbursements} />
            ) : (
              '--'
            )}
          </FeeDetailsHeaderValues>
        </div>
      </div>
    </div>
  );
};
